<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { attributeFilterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 grid w-full max-w-3xl overflow-visible rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl lg:w-[750px] dark:border-n-strong"
    role="dialog"
    :aria-label="filterModalHeaderTitle"
  >
    <header class="flex items-start justify-between gap-3 px-5 pb-3 pt-5">
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <span class="i-hugeicons-filter-horizontal size-[18px]" />
        </span>
        <div class="min-w-0">
          <h3 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ filterModalHeaderTitle }}
          </h3>
          <p class="mb-0 mt-0.5 text-xs text-n-slate-11">
            {{
              isFolderView
                ? t('FILTER.CUSTOM_VIEWS_SUBTITLE')
                : t('FILTER.MODAL_DESCRIPTION')
            }}
          </p>
        </div>
      </div>
      <Button
        v-tooltip.top="t('FILTER.CLOSE')"
        xs
        ghost
        slate
        icon="i-hugeicons-cancel-01"
        :aria-label="t('FILTER.CLOSE')"
        @click="emit('close')"
      />
    </header>
    <div v-if="props.isFolderView" class="px-5 pb-3">
      <Input
        v-model="folderNameLocal"
        :label="t('FILTER.FOLDER_LABEL')"
        :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
      />
    </div>
    <ul
      class="mb-0 grid max-h-[60vh] list-none gap-2 overflow-visible px-5 pb-4"
    >
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="attributeFilterTypes"
          :show-query-operator="false"
          :position="index + 1"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="attributeFilterTypes"
          :position="index + 1"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <footer
      class="flex flex-wrap items-center justify-between gap-2 rounded-b-2xl border-t border-n-weak bg-n-solid-2 px-5 py-3"
    >
      <Button
        sm
        ghost
        slate
        icon="i-hugeicons-add-01"
        :label="$t('FILTER.ADD_NEW_FILTER')"
        @click="addFilter"
      />
      <div class="flex flex-wrap gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </footer>
  </div>
</template>
